export interface PdfTextItem {
    text: string;
    size?: number;
    gapAfter?: number;
}

export interface PdfPageSpec {
    title?: string;
    lines?: Array<{ label: string; value: string }>;
    items?: PdfTextItem[];
}

function escapePdfText(input: string): string {
    return (input || '')
        .replace(/[^\x20-\x7E]/g, '?')
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)');
}

function resolveItems(pageIndex: number, pageCount: number, spec: PdfPageSpec): PdfTextItem[] {
    if (spec.items && spec.items.length) {
        const withFooter: PdfTextItem[] = spec.items.slice();
        withFooter.push({ text: '', gapAfter: 18 });
        withFooter.push({ text: 'FICTIONAL STAR WARS SAMPLE DATA - NOT FOR FILING', size: 9, gapAfter: 10 });
        withFooter.push({ text: 'PAGE ' + (pageIndex + 1) + ' OF ' + pageCount, size: 10, gapAfter: 8 });
        return withFooter;
    }
    const items: PdfTextItem[] = [];
    items.push({ text: spec.title || 'NKR Find Lab Document', size: 16, gapAfter: 14 });
    items.push({ text: 'PAGE ' + (pageIndex + 1) + ' OF ' + pageCount, size: 11, gapAfter: 18 });
    const lines = spec.lines || [];
    for (let i = 0; i < lines.length; i++) {
        items.push({ text: lines[i].label + ': ' + lines[i].value, size: 11, gapAfter: 12 });
    }
    items.push({ text: 'FICTIONAL STAR WARS SAMPLE DATA - NOT FOR FILING', size: 9, gapAfter: 8 });
    return items;
}

function buildPageStream(pageIndex: number, pageCount: number, spec: PdfPageSpec): string {
    const items = resolveItems(pageIndex, pageCount, spec);
    const commands: string[] = [];
    commands.push('BT');
    commands.push('/F1 12 Tf');
    commands.push('50 760 Td');
    let currentSize = 12;
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const size = item.size != null ? item.size : 11;
        if (size !== currentSize) {
            commands.push(`/F1 ${size} Tf`);
            currentSize = size;
        }
        if (item.text) {
            commands.push(`(${escapePdfText(item.text)}) Tj`);
        }
        const gap = item.gapAfter != null ? item.gapAfter : 14;
        commands.push(`0 -${gap} Td`);
    }
    commands.push('ET');
    return commands.join('\n');
}

export function buildSearchLabPdf(pages: PdfPageSpec[]): Blob {
    const pageCount = Math.max(pages.length, 1);
    const normalized = pages.length > 0 ? pages : [{ title: 'Empty Lab Document', lines: [] }];
    const objects: string[] = [];
    objects.push('<< /Type /Catalog /Pages 2 0 R >>');

    const kids: string[] = [];
    for (let i = 0; i < pageCount; i++) {
        kids.push(`${3 + i * 2} 0 R`);
    }
    objects.push(`<< /Type /Pages /Kids [${kids.join(' ')}] /Count ${pageCount} >>`);

    for (let i = 0; i < pageCount; i++) {
        const contentObjNum = 4 + i * 2;
        objects.push(
            `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents ${contentObjNum} 0 R /Resources << /Font << /F1 ${3 + pageCount * 2} 0 R >> >> >>`
        );
        const stream = buildPageStream(i, pageCount, normalized[i]);
        objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
    }

    objects.push('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');

    let pdf = '%PDF-1.4\n';
    const offsets: number[] = [0];
    for (let i = 0; i < objects.length; i++) {
        offsets.push(pdf.length);
        pdf += `${i + 1} 0 obj\n${objects[i]}\nendobj\n`;
    }
    const xrefPos = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';
    for (let i = 1; i < offsets.length; i++) {
        pdf += `${offsets[i].toString().padStart(10, '0')} 00000 n \n`;
    }
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefPos}\n%%EOF`;

    return new Blob([pdf], { type: 'application/pdf' });
}

export function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { }
    }, 1000);
}

export function t(text: string, size?: number, gapAfter?: number): PdfTextItem {
    return { text, size, gapAfter };
}

export function kv(label: string, value: string, size?: number): PdfTextItem {
    return { text: label + ': ' + value, size: size != null ? size : 11, gapAfter: 12 };
}

export function rule(): PdfTextItem {
    return { text: '------------------------------------------------------------', size: 10, gapAfter: 12 };
}
